import { useMemo } from 'react';
import { connect, FormItemComponentProps } from 'react-antd-formutil';
import FieldSelect from 'components/FieldSelect';
import FormItem from 'components/FormItem';
import API from 'utils/API';
import http from 'utils/http';
import withPreload from 'utils/withPreload';

export interface StateItem {
    abbr: string;
    name: string;
}

const requestCache: {
    usStates?: Promise<StateItem[]>;
} = {};

export function getUSStates() {
    return (
        requestCache.usStates ||
        (requestCache.usStates = http
            .get<StateItem[]>(API.CUSTOMER.fields.stateUS())
            .then(({ data }) => data)
            .catch(error => {
                delete requestCache.usStates;

                throw error;
            }))
    );
}

interface StatesSelectProps extends Omit<FormItemComponentProps, 'children'> {
    disabled?: boolean;
    placeholder?: string;
    usStates: StateItem[];
    line?: boolean;
    label?: string;
    filterList?: string[];
}

const StatesSelect: React.FC<StatesSelectProps> = ({
    name = 'home_state_code',
    label = __(`State / Province`),
    line,
    placeholder = __('Please Select'),
    disabled,
    usStates,
    filterList,
    $defaultValue,
    className
}) => {
    const filterData = useMemo(() => {
        if (filterList?.length) {
            return filterList.reduce<StateItem[]>((prev, cur) => {
                const country = usStates.find(item => item.abbr === cur);

                if (country) {
                    prev.push(country);
                }

                return prev;
            }, []);
        }

        return usStates;
    }, [usStates, filterList]);

    return (
        <FormItem
            className={className}
            label={label}
            name={name}
            line={line}
            $defaultValue={$defaultValue}
            required={!disabled}
            validMessage={{ required: __('Please select a state/province') }}>
            <FieldSelect
                placeholder={placeholder}
                disabled={disabled}
                label={label}
                data={filterData.map(item => ({
                    key: item.abbr,
                    value: item.abbr,
                    label: item.name
                }))}
            />
        </FormItem>
    );
};

export default withPreload<'usStates'>(() => getUSStates(), {
    prop: 'usStates'
})(connect(StatesSelect));
